/*----------------------------------
- SALES PAGE
----------------------------------*/

/**
 * @docs docs/features/sales-page
 * @adr  ADR-0067
 * @adr  ADR-0028
 * @rule The shared sales page on the website (`surface="website"`): the newest 20 usable sales,
 *       never sorted, filtered, searched or paged for a visitor; every control members use opens
 *       the signup wall (`openSalesWall`) with the Product view it promised. The search box runs
 *       the one lookup a day through the website's gateway; a second one opens the wall. A row
 *       click and a locked lane are tracked before their wall opens; the website draws no lane
 *       details popover, so it tracks none.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect, useMemo, useState } from 'preact/hooks';
import { SALES_PATH, type PublicSalesPage, type SalesLookupResult } from '@uniquedomains/components/contracts/sales/public';
import SalesPage from '@uniquedomains/components/sales/SalesPage';
import { resolveSalesAuthIntent } from '@uniquedomains/components/sales/salesModel';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { SALES_PAGE_CONTENT } from '@/client/catalogs/landing/sales/pageContent';
import HomeAskAiSection from '@/client/pages/landing/components/ask-ai/HomeAskAiSection';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildSalesGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import RelatedLinks from '@/client/pages/landing/components/navigation/RelatedLinks';
import NewsletterInkBand from '@/client/pages/landing/components/newsletter/NewsletterInkBand';
import { PAGE_CONTAINER_CLASS, WIDE_PAGE_CONTAINER_CLASS } from '@/client/pages/landing/components/shared/pageStyles';
import { track } from '@/client/pages/landing/lib/analytics';
import { usePublishedTldPaths } from '@/client/pages/landing/tld/lib/usePublishedTldPaths';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { getHumanPageRelationships } from '@/common/catalogs/landing/internalLinks';
import { appErrorReporter } from '@/common/libs/errorReporting';
import { SALES_LOOKUP_GATEWAY_PATH } from '@/common/contracts/salesGateway';
import { buildSalesAskAiContext } from '../lib/salesAskAi';
import { openSalesWall, SALES_TRACKING_PAGE } from '../lib/salesConversion';
import { buildSalesWebsiteHrefs } from '../lib/salesHrefs';
import SalesLookupCard from './SalesLookupCard';
import SalesPersonasCard from './SalesPersonasCard';
import '../../shared.css';

/*----------------------------------
- CONSTANTS
----------------------------------*/

const SUBSCRIBE_ANCHOR_ID = 'subscribe';

/** The phone bar's primary action: the alert wall, back to `/sales#alerts`. */
const ALERT_INTENT = resolveSalesAuthIntent('alert', null, 'sales_mobile_bar');

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function SalesLandingPage({ page, navigation, canonicalUrl }: { page: PublicSalesPage; navigation?: LandingHeaderDomainsNavigation | null; canonicalUrl: string }) {
    const content = SALES_PAGE_CONTENT;
    const publishedPaths = usePublishedTldPaths();
    const [lookup, setLookup] = useState<SalesLookupResult | null>(null);
    const relatedLinks = getHumanPageRelationships(SALES_PATH).map((relationship) => ({ label: relationship.label, href: relationship.targetPath }));
    const geoContext = useMemo(() => buildSalesGeoContext({ dataLastChecked: page.freshness.today }), [page.freshness.today]);
    const askAiContext = useMemo(() => buildSalesAskAiContext({ page, canonicalUrl, geoContext }), [page, canonicalUrl, geoContext]);

    useEffect(() => {
        track('page_view', { page: SALES_TRACKING_PAGE, path: window.location.pathname });
    }, []);

    const onLookup = (word: string) => {
        void fetch(SALES_LOOKUP_GATEWAY_PATH, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({ word }),
        })
            // The website's own gateway (`server/routes/sales.ts`) shapes the answer; a 503 carries
            // no lookup, so the page stays as it was.
            .then((response): Promise<SalesLookupResult> | null => (response.ok ? response.json() : null))
            .then((body) => {
                if (!body) return;
                track('sales_word_lookup', { status: body.status });
                if (body.status === 'gate') {
                    openSalesWall(resolveSalesAuthIntent('lookup', body.word, 'lookup'));
                    return;
                }
                setLookup(body);
            })
            // The lookup is a convenience: a failed call leaves the page as it was, and the next
            // submit tries again; the error reaches the app's reporter, never the render.
            .catch((error) => appErrorReporter.app.handleError(error));
    };

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation ?? EMPTY_LANDING_NAVIGATION}
                    currentPath={SALES_PATH}
                    trackingPage={SALES_TRACKING_PAGE}
                    mobileActions={{
                        primary: { label: content.mobileAction, onClick: () => openSalesWall(ALERT_INTENT), testId: 'sales-mobile-alert' },
                    }}
                />
                <main id="top" data-testid="sales-landing">
                    <div class={`${WIDE_PAGE_CONTAINER_CLASS} pt-7 lg:pt-11`}>
                        <SalesPage
                            surface="website"
                            page={page}
                            member={null}
                            copy={content.copy}
                            hrefs={buildSalesWebsiteHrefs(publishedPaths)}
                            onAuthIntent={openSalesWall}
                            onLookup={onLookup}
                            onRowClick={(row) => track('sales_row_click', { surface: 'website', inCatalog: row.inCatalog })}
                            onLaneOpen={(lane) => track('sales_lane_opened', { lane, surface: 'website' })}
                            lookupSlot={lookup ? <SalesLookupCard result={lookup} onOpen={(word) => openSalesWall(resolveSalesAuthIntent('lookup', word, 'lookup_card'))} /> : null}
                            canonicalUrl={canonicalUrl}
                            testIdPrefix="sales"
                        />
                    </div>
                    <div class={`${PAGE_CONTAINER_CLASS} pt-10 lg:pt-14`}>
                        <SalesPersonasCard />
                    </div>
                    <div class={`${PAGE_CONTAINER_CLASS} pt-6 lg:pt-8`}>
                        <NewsletterInkBand anchorId={SUBSCRIBE_ANCHOR_ID} source="website_sales" heading={content.newsletter.heading} body={content.newsletter.body} />
                    </div>
                    <RelatedLinks links={relatedLinks} title={content.relatedLinksTitle} />
                    <HomeAskAiSection context={askAiContext} />
                    <PageGeoBlock className="pb-4" geo={geoContext} surface="sales" />
                </main>
                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage={SALES_TRACKING_PAGE} />
            </div>
        </div>
    );
}
